import { BrowserProvider, Contract } from 'ethers';

const USDC_ADDRESS = '0x3c499c542cEF5E3811e1192ce70d8cC03d5c3359';
const USDT_ADDRESS = '0xc2132D05D31c914a87C6611C10748AEb04B58e8F';
const POLYGON_CHAIN_ID = '0x89'; // 137

const ERC20_ABI = ['function transfer(address to, uint256 amount) returns (bool)'];

function getApiBase(): string {
  const envUrl = (import.meta as any).env?.VITE_API_URL;
  if (envUrl) return envUrl;
  if (typeof window !== 'undefined' && window.location?.hostname !== 'localhost') return '';
  return 'http://localhost:4000';
}

export interface Pack {
  id: string;
  runes: number;
  priceUsd: number;
}

export interface PurchaseResult {
  success: boolean;
  runes: number;
}

export class PaymentService {
  private readonly apiBase: string;

  constructor(
    private readonly authToken: string,
    private readonly ethereum: any = (typeof window !== 'undefined' ? (window as any).ethereum : undefined),
    apiBase?: string
  ) {
    this.apiBase = apiBase ?? getApiBase();
  }

  async fetchPacks(): Promise<Pack[]> {
    const res = await fetch(`${this.apiBase}/api/shop/packs`);
    const data = await res.json() as { packs: Pack[] };
    return data.packs;
  }

  async purchase(packId: string, token: 'ETH' | 'USDC' | 'USDT'): Promise<PurchaseResult> {
    if (!this.ethereum) {
      throw new Error('MetaMask is not installed. Please install MetaMask to purchase runes.');
    }

    // Connect wallet
    await this.ethereum.request({ method: 'eth_requestAccounts' });

    // Ensure Polygon network
    await this.ethereum.request({
      method: 'wallet_switchEthereumChain',
      params: [{ chainId: POLYGON_CHAIN_ID }],
    });

    // Create purchase order on server
    const orderRes = await fetch(`${this.apiBase}/api/shop/purchase`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: this.authToken },
      body: JSON.stringify({ packId, token }),
    });
    if (!orderRes.ok) {
      const err = await orderRes.json().catch(() => ({ error: 'Failed to create order' })) as { error?: string };
      throw new Error(err.error ?? 'Failed to create order');
    }
    const order = await orderRes.json() as {
      orderId: string;
      paymentAddress: string;
      token: string;
      chainId: number;
      expectedAmount: string;
      runes: number;
    };

    // Send transaction via MetaMask
    const provider = new BrowserProvider(this.ethereum);
    const signer = await provider.getSigner();
    let txHash: string;

    if (token === 'ETH') {
      const tx = await signer.sendTransaction({
        to: order.paymentAddress,
        value: BigInt(order.expectedAmount),
      });
      txHash = tx.hash;
      await tx.wait();
    } else {
      const contractAddress = token === 'USDC' ? USDC_ADDRESS : USDT_ADDRESS;
      const contract = new Contract(contractAddress, ERC20_ABI, signer);
      const tx = await contract.transfer(order.paymentAddress, BigInt(order.expectedAmount));
      txHash = tx.hash;
      await tx.wait();
    }

    // Verify payment on server
    const verifyRes = await fetch(`${this.apiBase}/api/shop/verify`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: this.authToken },
      body: JSON.stringify({ orderId: order.orderId, txHash }),
    });
    if (!verifyRes.ok) {
      const err = await verifyRes.json().catch(() => ({ error: 'Verification failed' })) as { error?: string };
      throw new Error(err.error ?? 'Verification failed');
    }

    return await verifyRes.json() as PurchaseResult;
  }
}
